import { getClassLevel } from './classIdentity.mjs';
import { isIncapacitated } from './barbarianRage.mjs';
export const RECKLESS = 'reckless-attack';
export const RECKLESS_READY = 'barbarian:reckless-ready';
export const FIRST_ATTACK = 'barbarian:first-attack';
export const FRENZY_USED = 'barbarian:berserker:frenzy-used';
const definitions = new Set([RECKLESS, RECKLESS_READY, FIRST_ATTACK, FRENZY_USED]);
export const isRecklessEffect = effect => definitions.has(effect?.definitionId);
export const activeCombatantId = combat => String(combat?.participants?.[combat.activeTurn]?.characterId || combat?.participants?.[combat.activeTurn]?.enemyId || '');
export function resolveRecklessActorId(character, combat, actorId) {
  const ids = [actorId, character?._id, character?.characterId].filter(Boolean).map(String);
  return (combat?.participants || []).map(p => String(p.characterId || p.enemyId)).find(id => ids.includes(id)) || String(actorId || '');
}
export function recklessWindow(combat = {}, actorId) {
  const effects = (combat?.activeEffects || []).filter(e => String(e.targetCombatantId) === String(actorId));
  const ownTurn = Number.isInteger(combat?.activeTurn) && activeCombatantId(combat) === String(actorId);
  return { ownTurn, active: effects.some(e => e.definitionId === RECKLESS),
    ready: ownTurn && effects.some(e => e.definitionId === RECKLESS_READY && e.turnSequence === combat.turnSequence),
    firstAttackMade: ownTurn && effects.some(e => e.definitionId === FIRST_ATTACK && e.turnSequence === combat.turnSequence) };
}
export function recklessEligibility(character, combat, actorId) {
  actorId = resolveRecklessActorId(character, combat, actorId);
  const window = recklessWindow(combat, actorId);
  const reason = getClassLevel(character, 'barbarian') < 2 ? 'Reckless Attack requires Barbarian level 2.'
    : !window.ownTurn ? 'Ready Reckless Attack on your own turn.'
    : window.active ? 'Reckless Attack is active until your next turn starts.'
    : window.firstAttackMade ? 'Choose Reckless Attack before your first attack roll this turn.'
    : isIncapacitated({ ...character, combatantId: actorId }, combat) ? 'Cannot ready Reckless Attack while Incapacitated.' : '';
  return { ...window, allowed: !reason, reason };
}
export function recklessEffect(definitionId, actorId, sequence) {
  return { id: `${definitionId}:${actorId}:${sequence}`, definitionId, sourceCombatantId: actorId, targetCombatantId: actorId,
    turnSequence: sequence, name: definitionId === RECKLESS ? 'Reckless Attack' : definitionId === RECKLESS_READY ? 'Reckless Attack Ready' : definitionId === FRENZY_USED ? 'Frenzy Used' : 'First Attack Rolled',
    userVisible: definitionId === RECKLESS, stackKey: `${definitionId}:${actorId}`, stackPolicy: 'replace',
    expiration: { type: 'sourceTurn', combatantId: actorId, boundary: definitionId === RECKLESS_READY ? 'end' : 'start', remainingOccurrences: 1 },
    ...(definitionId === RECKLESS ? { description: 'Advantage on Strength attack rolls; attacks against you have Advantage until your next turn starts.' } : {}) };
}
// Combat snapshots cannot forge or remove server-owned declarations/roll markers.
export function reconcileRecklessEffects(previous = {}, next = {}) {
  const changed = previous.turnSequence !== next.turnSequence;
  const retained = !next.participants?.length ? [] : (previous.activeEffects || []).filter(isRecklessEffect).filter(e => {
    if (!next.participants.some(p => String(p.characterId || p.enemyId) === String(e.targetCombatantId))) return false;
    return !(changed && (e.definitionId === RECKLESS_READY ? activeCombatantId(previous) : activeCombatantId(next)) === String(e.targetCombatantId));
  });
  return [...(next.activeEffects || []).filter(e => !isRecklessEffect(e)), ...retained];
}
